<form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col max-h-[82vh]">
  <div class="flex flex-col gap-4 p-5 overflow-y-auto">
    <!-- Top Context Card -->
    @if (mode() === 'split') {
      <div class="p-3.5 rounded-xl bg-surface border border-border flex items-center justify-between gap-3 flex-wrap">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-9 h-9 rounded-lg bg-primary-low-contrast text-primary flex items-center justify-center shrink-0">
            <lucide-icon name="ArrowRightLeft" size="16"></lucide-icon>
          </div>
          <div class="flex flex-col min-w-0">
            <app-text size="xs" color="muted">Splitting {{ outgoingSummary().legTitle }} (Outgoing)</app-text>
            <app-text size="sm" weight="semibold" class="truncate text-foreground">
              {{ outgoingSummary().driverName }} &middot; {{ outgoingSummary().equipment }}
            </app-text>
          </div>
        </div>
        <app-badge variant="primary" size="xs" title="Relay / Route Split"></app-badge>
      </div>
    } @else if (targetSegment(); as seg) {
      <div class="p-3.5 rounded-xl bg-surface border border-border flex items-center justify-between gap-3 flex-wrap">
        <div class="flex items-center gap-2.5">
          <div class="w-9 h-9 rounded-lg bg-primary-low-contrast text-primary flex items-center justify-center shrink-0">
            <lucide-icon name="Route" size="16"></lucide-icon>
          </div>
          <div class="flex flex-col">
            <app-text size="sm" weight="semibold" class="text-foreground">
              Leg {{ seg.segmentNumber }} &middot; Stops {{ seg.fromStopNumber }}–{{ seg.toStopNumber }}
            </app-text>
            <app-text size="xs" color="muted">
              {{ seg.segmentMiles ? (seg.segmentMiles | number) + ' loaded mi' : 'Route leg assignment' }}
            </app-text>
          </div>
        </div>
        <app-badge
          [variant]="seg.status === 'completed' ? 'success' : seg.status === 'active' ? 'info' : 'warning'"
          size="xs"
          [title]="seg.status | titlecase"
        ></app-badge>
      </div>
    }

    <!-- Card 1: Where does the swap happen? (Split Mode Only) -->
    @if (mode() === 'split') {
      <app-form-card title="Where does the swap happen?" icon="MapPin">
        <app-form-field
          label="Split Point"
          hint="Split at an existing intermediate stop or insert a dedicated relay yard between stops"
          [required]="true"
        >
          <app-select
            [options]="splitPointOptions()"
            [sortOptions]="false"
            [ngModel]="splitPoint() + ''"
            (ngModelChange)="onSplitPointChange($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-select>
        </app-form-field>

        @if (isInsertingNewRelayStop()) {
          @if (hasSavedYards()) {
            <app-form-field label="Relay Location Source">
              <app-button-group
                [options]="locationTypeOptions"
                [ngModel]="locationType()"
                (ngModelChange)="onLocationTypeChange($event)"
                [ngModelOptions]="{ standalone: true }"
              ></app-button-group>
            </app-form-field>
          }

          @if (locationType() === 'saved' && hasSavedYards()) {
            <app-form-field label="Saved Yard / Terminal" [required]="true">
              <app-select
                [options]="yardOptions()"
                [ngModel]="selectedYardId()"
                (ngModelChange)="onYardSelect($event)"
                [ngModelOptions]="{ standalone: true }"
                placeholder="Select a saved yard or terminal"
              ></app-select>
            </app-form-field>

            @if (selectedYard(); as yard) {
              <div class="px-3.5 py-2.5 rounded-lg bg-surface-high border border-border flex items-center justify-between gap-2">
                <div class="flex flex-col min-w-0">
                  <app-text size="xs" weight="semibold" class="text-foreground truncate">
                    {{ yard.name }}
                  </app-text>
                  <app-text size="xs" color="muted" class="truncate">
                    {{ yard.address.street ? yard.address.street + ', ' : '' }}{{ formatCityStateZip(yard.address) }}
                  </app-text>
                </div>
                <app-badge [title]="yard.isTerminal ? 'Terminal' : 'Drop Yard'" variant="secondary" size="xs"></app-badge>
              </div>
            }
          } @else {
            <app-form-field label="Relay Facility / Yard Name" [required]="true">
              <app-input
                formControlName="companyName"
                placeholder="e.g. Love's Travel Stop or Carrier Drop Yard"
              ></app-input>
            </app-form-field>

            <app-address-form formControlName="address"></app-address-form>
          }
        }
      </app-form-card>
    }

    <!-- Card 2: Driver & Equipment Assignment -->
    <app-form-card
      [title]="mode() === 'split' ? 'Oncoming Driver & Equipment' : 'Assigned Driver & Equipment'"
      icon="Truck"
    >
      @if (mode() === 'split') {
        <app-form-field
          label="Swap Type"
          hint="Equipment not being swapped carries forward automatically from the outgoing leg"
        >
          <app-button-group
            [options]="exchangeTypeOptions"
            [ngModel]="exchangeType()"
            (ngModelChange)="onExchangeTypeChange($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-button-group>
        </app-form-field>
      }

      @if (mode() === 'edit' || exchangeType() !== 'equipment_only') {
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-2">
            <app-text size="xs" color="muted">
              {{ mode() === 'split' ? 'Who is taking over from this point onward?' : 'Who is driving this leg?' }}
            </app-text>
            <app-checkbox
              label="Team leg (add co-driver)"
              size="sm"
              [ngModel]="isTeamLeg()"
              (ngModelChange)="onToggleTeam($event)"
              [ngModelOptions]="{ standalone: true }"
            ></app-checkbox>
          </div>

          <app-form-field
            [label]="mode() === 'split' ? 'Oncoming Primary Driver' : 'Primary Driver'"
            [required]="mode() === 'split'"
          >
            <app-select
              formControlName="driverId"
              [options]="driverOptions()"
              placeholder="Select driver"
            ></app-select>
          </app-form-field>

          <app-driver-credential-warning
            [driver]="selectedDriver1()"
            [loadDate]="loadEndDate()"
          ></app-driver-credential-warning>

          @if (driverConflicts().length > 0) {
            <div class="p-2.5 rounded-lg bg-surface-high border border-warning flex items-start gap-2">
              <lucide-icon name="AlertTriangle" size="14" class="text-warning shrink-0 mt-0.5"></lucide-icon>
              <app-text size="xs" class="text-warning">
                Driver is also assigned on Load #{{ driverConflicts()[0].loadExternalId || driverConflicts()[0].loadId }}.
              </app-text>
            </div>
          }

          @if (isTeamLeg()) {
            <app-form-field label="Co-Driver (Team Driver 2)">
              <app-select
                formControlName="driver2Id"
                [options]="driverOptions()"
                placeholder="Select team co-driver"
              ></app-select>
            </app-form-field>
            <app-driver-credential-warning
              [driver]="selectedDriver2()"
              [loadDate]="loadEndDate()"
            ></app-driver-credential-warning>
          }
        </div>
      }

      @if (mode() === 'edit' || exchangeType() !== 'driver_only') {
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <app-form-field
            [label]="mode() === 'split' ? 'Oncoming Truck' : 'Truck'"
            [required]="mode() === 'split'"
          >
            <app-select
              formControlName="truckId"
              [options]="truckOptions()"
              placeholder="Select truck"
            ></app-select>
          </app-form-field>

          <app-form-field [label]="mode() === 'split' ? 'Oncoming Trailer' : 'Trailer'">
            <app-select
              formControlName="trailerId"
              [options]="trailerOptions()"
              placeholder="Select trailer"
            ></app-select>
          </app-form-field>
        </div>

        @if (truckConflicts().length > 0) {
          <div class="p-2.5 rounded-lg bg-surface-high border border-warning flex items-start gap-2">
            <lucide-icon name="AlertTriangle" size="14" class="text-warning shrink-0 mt-0.5"></lucide-icon>
            <app-text size="xs" class="text-warning">
              Truck has an overlapping assignment on Load #{{ truckConflicts()[0].loadExternalId || truckConflicts()[0].loadId }}.
            </app-text>
          </div>
        }
      }

      @if (showCommercialBoundaryDuty()) {
        <div class="pt-2 border-t border-border">
          <app-form-field
            [label]="'Stop #' + existingSplitStop()?.stopNumber + ' Dock Attribution'"
            [hint]="'Which driver handles loading/unloading at ' + (existingSplitStop()?.companyName || 'this stop') + ' (earns stop pay, detention & lumper reimbursement)?'"
          >
            <app-button-group
              [options]="servicedByOptions"
              [ngModel]="servicedBy()"
              (ngModelChange)="servicedBy.set($event)"
              [ngModelOptions]="{ standalone: true }"
            ></app-button-group>
          </app-form-field>
        </div>
      }
    </app-form-card>

    <!-- Card 3: Relay Reason & Handoff Instructions (for Split or Leg 2+) -->
    @if (mode() === 'split' || (targetSegment()?.segmentNumber || 1) > 1) {
      <app-form-card title="Relay Reason & Handoff Instructions" icon="ArrowRightLeft">
        <app-form-field
          label="Relay Reason"
          hint="Select why the driver or equipment is changing at this point"
        >
          <app-select-chip
            [options]="reasonChipOptions"
            [ngModel]="selectedReasonChip()"
            (ngModelChange)="onSelectReasonChip($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-select-chip>
        </app-form-field>

        @if (selectedReasonChip() === 'Other') {
          <app-form-field label="Specify Reason" [required]="true">
            <app-input
              formControlName="customExchangeReason"
              placeholder="Enter custom reason for relay / handoff"
            ></app-input>
          </app-form-field>
        }

        <app-form-field
          label="Handoff Instructions (Optional)"
          hint="Shown to both outgoing and oncoming drivers in the Haulverse Driver app"
        >
          <app-textarea
            formControlName="exchangeNotes"
            [rows]="2"
            placeholder="e.g. Drop loaded trailer in Row B, verify seal # intact, leave BOL in nose box"
          ></app-textarea>
        </app-form-field>
      </app-form-card>
    }
  </div>

  <div class="flex items-center justify-end gap-3 p-4 border-t border-border bg-surface-low rounded-b-xl">
    <app-button variant="ghost" type="button" (click)="cancel()" [disabled]="isSaving()">
      Cancel
    </app-button>
    <app-button variant="primary" type="submit" [loading]="isSaving()" icon="Check">
      {{ mode() === 'split' ? 'Confirm Route Split' : 'Save Leg Changes' }}
    </app-button>
  </div>
</form>
